import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import axios from "axios";

const BookAppointment = ({ show, onHide, userId, onAppointmentBooked }) => {
  const [form, setForm] = useState({
    doctorId: "",
    date: "",
    time: "",
    explanation: "",
    file: null,
  });

  const [doctors, setDoctors] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [messagePopup, setMessagePopup] = useState({ show: false, message: "" });

  const showMessage = (msg) => setMessagePopup({ show: true, message: msg });
  const closeMessage = () => setMessagePopup({ show: false, message: "" });

  useEffect(() => {
    const fetchDoctors = async () => {
      setLoading(true);
      try {
        const res = await axios.get("https://ricgop.online/api/get-doctors.php");
        setDoctors(res.data);
      } catch (err) {
        showMessage("Failed to load doctors.");
      }
      setLoading(false);
    };
    fetchDoctors();
  }, []);

  useEffect(() => {
    const fetchSlots = async () => {
      if (form.doctorId && form.date) {
        try {
          const res = await axios.get(
            `https://ricgop.online/api/get_slots.php?doctor_id=${form.doctorId}&date=${form.date}`
          );

          const today = new Date();
          const selectedDate = new Date(form.date);
          today.setHours(0, 0, 0, 0);
          selectedDate.setHours(0, 0, 0, 0);

          let availableSlots = res.data?.slots || [];

          if (selectedDate.getTime() === today.getTime()) {
            const currentTime = new Date().toTimeString().slice(0, 5);
            availableSlots = availableSlots.filter((slot) => slot.time > currentTime);
          }

          if (selectedDate < today) {
            availableSlots = [];
          }

          setSlots(availableSlots);
        } catch (err) {
          console.error("Failed to fetch slots", err);
          setSlots([]);
        }
      } else {
        setSlots([]);
      }
    };

    fetchSlots();
  }, [form.doctorId, form.date]);

  useEffect(() => {
    if (messagePopup.show) {
      const timer = setTimeout(() => closeMessage(), 3000);
      return () => clearTimeout(timer);
    }
  }, [messagePopup.show]);

  const handleSubmit = async () => {
    const { doctorId, date, time } = form;
    if (!doctorId || !date || !time) {
      showMessage("Please fill all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      const selectedDoctor = doctors.find((doc) => doc.id === doctorId);

      formData.append("doctorId", doctorId);
      formData.append("doctorName", selectedDoctor?.name || "");
      formData.append("userId", userId);
      formData.append("date", date);
      formData.append("time", time);
      formData.append("explanation", form.explanation);
      if (form.file) formData.append("file", form.file);

      const response = await axios.post(
        "https://ricgop.online/api/create-appointment.php",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.success) {
        showMessage("Appointment booked successfully!");
        onHide();
        onAppointmentBooked?.();
        setForm({ doctorId: "", date: "", time: "", explanation: "", file: null });
      } else {
        showMessage("Error: " + response.data.message);
      }
    } catch (error) {
      console.error("Booking failed:", error);
      showMessage("Booking failed.");
    }
    setSubmitting(false);
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="lg" backdrop="static" className="booking-modal">
        <Modal.Header closeButton>
          <Modal.Title>Book Appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center my-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <Form>
              <Row className="gy-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Select Doctor</Form.Label>
                    <Form.Select
                      value={form.doctorId}
                      onChange={(e) => setForm({ ...form, doctorId: e.target.value })}
                    >
                      <option value="">Select a Doctor</option>
                      {doctors.map((doc) => (
                        <option key={doc.id} value={doc.id}>
                          {doc.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Select Date</Form.Label>
                    <Form.Control
                      type="date"
                      min={today}
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Available Time Slots</Form.Label>
                    <Form.Select
                      value={form.time}
                      onChange={(e) => setForm({ ...form, time: e.target.value })}
                      disabled={!slots.length}
                    >
                      <option value="">
                        {slots.length ? "Select a Slot" : "No slots available"}
                      </option>
                      {slots.map((slot, idx) => (
                        <option key={idx} value={slot.time}>
                          {slot.time}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Explanation</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={form.explanation}
                      onChange={(e) => setForm({ ...form, explanation: e.target.value })}
                      placeholder="Additional info (optional)"
                    />
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Upload Document</Form.Label>
                    <Form.Control
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                    />
                    <Form.Text className="text-muted">
                      Allowed file types: PDF, JPG, PNG, DOC
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" style={{ minWidth: "150px" }} onClick={handleSubmit} disabled={loading || submitting}>
            {submitting ? "Booking..." : "Book Appointment"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={messagePopup.show} onHide={closeMessage} centered className="message-popup-modal">
        <Modal.Body>{messagePopup.message}</Modal.Body>
     <style>{`
  .booking-modal.modal.show,
  .message-popup-modal.modal.show {
    display: flex !important;
    align-items: center;
    justify-content: center;
  }

.booking-modal .modal-dialog {
  max-width: 50% !important;
  min-width: 300px;
  width: 100%;
  margin: auto;
  transition: none !important;
}

.booking-modal .modal-content {
  width: 100%;
  border-radius: 1rem;
  padding: 1rem 1.5rem;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  min-height: 550px;
}

  /* Message Popup Modal content */
  .message-popup-modal .modal-dialog {
    max-width: 30vw !important;
  }

  .message-popup-modal .modal-content {
    width: 100%;
    padding: 1.5rem 2rem;
    color: #333;
    font-weight: 600;
    font-size: 1.1rem;
    text-align: center;
    border-radius: 15px;
    background: #f9f9f9;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }

  .booking-modal .btn-primary,
.booking-modal .btn-secondary {
  min-width: 150px;
  text-align: center;
}
  
  /* Buttons */
  .booking-modal .btn-primary {
    background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%);
    border: none;
    font-weight: 600;
    padding: 0.65rem 1.8rem;
    border-radius: 12px;
    transition: background 0.3s ease, box-shadow 0.3s ease;
    box-shadow: 0 4px 15px rgba(101, 48, 255, 0.4);
  }

  .booking-modal .btn-primary:hover {
    background: linear-gradient(135deg, #2575fc 0%, #6a11cb 100%);
    box-shadow: 0 6px 20px rgba(101, 48, 255, 0.6);
  }

  .booking-modal .btn-secondary {
    background: #e0e0e0;
    color: #555;
    font-weight: 600;
    padding: 0.6rem 1.5rem;
    border-radius: 12px;
    border: none;
    transition: background 0.3s ease;
  }

  .booking-modal .btn-secondary:hover {
    background: #cfcfcf;
    color: #444;
  }

  /* Form elements */
  .booking-modal .form-label {
    font-weight: 600;
    color: #444;
  }

  .booking-modal .form-control,
  .booking-modal .form-select {
    border-radius: 10px;
    border: 1.5px solid #ddd;
    padding: 0.5rem 0.75rem;
    font-size: 1rem;
    transition: border-color 0.3s ease;
  }

  .booking-modal .form-control:focus,
  .booking-modal .form-select:focus {
    border-color: #6a11cb;
    box-shadow: none;
  }
    @media (max-width: 768px) {
  .booking-modal .modal-dialog {
    max-width: 90% !important;
  }
}
`}</style>
      </Modal>
    </>
  );
};

export default BookAppointment;
